import { zodResolver } from '@hookform/resolvers/zod'
import { CaretLeft } from '@phosphor-icons/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import cx from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useSetRecoilState } from 'recoil'

import { Button } from '@/components/button'
import CampaignButtons from '@/components/campaign-buttons'
import { FormInput, FormSelect } from '@/components/form-fields'
import WYSIWYG from '@/components/form/custom-wysiwyg-editor'
import {
  ECFlowScreensType,
  ECFlowTextsFormType,
  EcFlowButtonKey,
  EcFlowFormValues,
  EcFlowScreenTexts,
  TypeOfUser,
  ecFlowButtonsSchema,
  ecFlowErrorLanguageSchema,
  ecFlowFormSchema,
} from '@/schema/ec-flow-texts.schema'

import { useCampaignClient } from '@/lib/api/ts-rest-client'
import { GetCampaignFragment } from '@/types/campaign'
import i18n from '../../i18n'
import { activeStepState } from '../../store/atoms/active-step'

import {
  buttonFallbackKeys,
  screenButtonKeys,
} from '../../../../end-users/src/utils/ec-flow-texts'
import { userType } from '../../../../end-users/src/utils/user-type'
import { hasHappinessCheck } from '../../../../end-users/src/utils/happiness-check'

const buttonKeys = ecFlowButtonsSchema.keyof().options
// The Dutch text an end user sees when the campaign has no own text.
const standardButtonText = (key: EcFlowButtonKey) =>
  i18n.getFixedT('nl')(buttonFallbackKeys[key])

// Screens whose end-user texts follow the error language (errors, or the invite share message).
const errorLanguageScreens: string[] = [
  ECFlowScreensType.INCENTIVE,
  ECFlowScreensType.QUESTIONNAIRE,
  ECFlowScreensType.INVITE,
  ECFlowScreensType.HAPPINESS_CHECK,
]

type EcFlowTextsStructure = Record<
  string,
  Record<string, { nl?: EcFlowScreenTexts }>
>

export default function EndCustomerFlow({
  campaign,
}: {
  campaign: GetCampaignFragment
}) {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { pathname } = location
  const { t } = useTranslation()
  const setActiveStep = useSetRecoilState(activeStepState)

  const derivedUserType = userType(pathname)
  const [selectedUserType, setSelectedUserType] =
    useState<TypeOfUser>(derivedUserType)

  const campaignClient = useCampaignClient()
  const queryClient = useQueryClient()

  const createECFlowTextsMutation = useMutation({
    mutationFn: async (data: {
      campaignId: string
      ecFlowTexts: any
      reminders?: any
    }) => {
      const response = await campaignClient.createECFlowTexts.mutate({
        params: { id: data.campaignId },
        body: data,
      })
      if (response.status !== 200) {
        throw new Error('Failed to save EC flow texts')
      }
      return response
    },
    onSuccess: () => {
      // Invalidate campaign cache to refresh the preview
      queryClient.invalidateQueries({ queryKey: ['campaign', id] })
    },
  })

  const ecFlowData: ECFlowTextsFormType = useMemo(
    () => campaign.ecFlowTexts || {},
    [campaign.ecFlowTexts],
  )

  const currentScreenName = useMemo(
    () => pathname.split('/').pop() ?? ECFlowScreensType.HOME,
    [pathname],
  )

  const userTypeTexts = useMemo(
    () =>
      (ecFlowData.ecFlowTexts as EcFlowTextsStructure | undefined)?.[
        selectedUserType ?? ''
      ],
    [ecFlowData.ecFlowTexts, selectedUserType],
  )

  const screenValues = useMemo(
    () => userTypeTexts?.[currentScreenName]?.nl ?? {},
    [userTypeTexts, currentScreenName],
  )

  const defaultFormValues = useMemo<EcFlowFormValues>(
    () => ({
      header: screenValues?.header ?? '',
      text: screenValues?.text ?? '',
      termsAndConditions: screenValues?.termsAndConditions ?? '',
      termsAndConditionsEndUser: screenValues?.termsAndConditionsEndUser ?? '',
      termsAndConditionsReferral:
        screenValues?.termsAndConditionsReferral ?? '',
      termsAndConditionsSummary: screenValues?.termsAndConditionsSummary ?? '',
      buttons: Object.fromEntries(
        buttonKeys.map((key) => [
          key,
          ecFlowData.ecFlowTexts?.buttons?.[key] ?? standardButtonText(key),
        ]),
      ) as Record<EcFlowButtonKey, string>,
      errorLanguage: ecFlowData.ecFlowTexts?.errorLanguage ?? 'nl',
    }),
    [screenValues, ecFlowData.ecFlowTexts],
  )

  const { control, handleSubmit, reset } = useForm<EcFlowFormValues>({
    defaultValues: defaultFormValues,
    resolver: zodResolver(ecFlowFormSchema),
  })

  useEffect(() => {
    reset(defaultFormValues)
  }, [defaultFormValues, reset])

  useEffect(() => {
    const hasReferralIncentives =
      campaign?.referralCampaign && !campaign?.reuseSameIncentives
    const stepNumber = campaign?.referralCampaign
      ? hasReferralIncentives
        ? 9
        : 8
      : 7

    setActiveStep(stepNumber)
  }, [setActiveStep, campaign?.referralCampaign, campaign?.reuseSameIncentives])

  const nextScreenIndex =
    Object.values(ECFlowScreensType).findIndex(
      (value) => value === currentScreenName,
    ) + 1

  let nextScreenName = Object.values(ECFlowScreensType)[nextScreenIndex]
  const previousScreenIndex =
    Object.values(ECFlowScreensType).findIndex(
      (value) => value === currentScreenName,
    ) - 1
  let previousScreenName = Object.values(ECFlowScreensType)[previousScreenIndex]

  if (
    nextScreenName === ECFlowScreensType.INVITE &&
    (!campaign.referralCampaign ||
      selectedUserType === TypeOfUser.REFERRAL_USER)
  ) {
    nextScreenName = Object.values(ECFlowScreensType)[nextScreenIndex + 1]
  }

  if (
    previousScreenName === ECFlowScreensType.INVITE &&
    (!campaign.referralCampaign ||
      selectedUserType === TypeOfUser.REFERRAL_USER)
  ) {
    previousScreenName =
      Object.values(ECFlowScreensType)[previousScreenIndex - 1]
  }

  const shouldShowTextField = true
  // currentScreenName !== ECFlowScreensType.QUESTIONNAIRE &&
  //   currentScreenName !== ECFlowScreensType.HAPPINESS_CHECK
  const isQuestionnaireScreen =
    currentScreenName === ECFlowScreensType.QUESTIONNAIRE

  // The invite screen shows Next when there is a happiness check, else Finish (invite.tsx).
  const inviteHiddenKey = hasHappinessCheck(campaign) ? 'finish' : 'next'
  const visibleButtonKeys = buttonKeys.filter(
    (key) =>
      screenButtonKeys[currentScreenName]?.includes(key) &&
      !(
        currentScreenName === ECFlowScreensType.INVITE &&
        key === inviteHiddenKey
      ),
  )
  const showErrorLanguage = errorLanguageScreens.includes(currentScreenName)

  const onSubmit: SubmitHandler<EcFlowFormValues> = (formData, event) => {
    const buttonName = (
      (event?.nativeEvent as SubmitEvent) || undefined
    )?.submitter?.getAttribute('name')

    const payload: Record<string, string> = {
      header: formData.header ?? '',
    }

    if (shouldShowTextField) {
      payload.text = formData.text ?? ''
    }

    if (isQuestionnaireScreen) {
      payload.termsAndConditions = formData.termsAndConditions ?? ''
      payload.termsAndConditionsEndUser =
        formData.termsAndConditionsEndUser ?? ''
      payload.termsAndConditionsReferral =
        formData.termsAndConditionsReferral ?? ''
      payload.termsAndConditionsSummary =
        formData.termsAndConditionsSummary ?? ''
    }

    // Only a changed text is saved, so a campaign keeps following the standard text.
    const buttons = Object.fromEntries(
      Object.entries(formData.buttons ?? {}).filter(
        ([key, text]) =>
          text && text !== standardButtonText(key as EcFlowButtonKey),
      ),
    )

    createECFlowTextsMutation.mutate(
      {
        campaignId: campaign.id,
        ecFlowTexts: {
          ecFlowTexts: {
            ...ecFlowData.ecFlowTexts,
            buttons,
            errorLanguage: formData.errorLanguage,
            [selectedUserType ?? '']: {
              ...(userTypeTexts ?? {}),
              [currentScreenName]: { nl: payload },
            },
          },
          // Preserve existing reminders when updating flow texts
          reminders: ecFlowData?.reminders,
        },
      },
      {
        onSuccess() {
          if (buttonName === 'next') {
            let nextStep = nextScreenName
            if (
              !hasHappinessCheck(campaign) &&
              nextScreenName === ECFlowScreensType.HAPPINESS_CHECK
            ) {
              nextStep = ECFlowScreensType.SUCCESS
            }
            navigate(`/campaigns/ec-flow/${id}/${selectedUserType}/${nextStep}`)
          }
        },
      },
    )
  }

  return (
    <div className="w-full mx-auto px-2">
      <form onSubmit={handleSubmit(onSubmit)}>
        {campaign.referralCampaign && (
          <div className="flex w-3/4 gap-x-4 text-sm">
            <label
              onClick={() => {
                navigate(`/campaigns/ec-flow/${id}/${TypeOfUser.END_USER}/home`)
                setSelectedUserType(TypeOfUser.END_USER)
              }}
              className={cx(
                'flex flex-1 cursor-pointer rounded-lg border-2 border-border bg-white p-2 shadow-sm focus:outline-none hover:border-secondary',
                {
                  'border-black-100': selectedUserType === TypeOfUser.END_USER,
                },
              )}
              data-testid="user-type-option-end-user"
            >
              <input
                name={TypeOfUser.END_USER}
                type="radio"
                value={TypeOfUser.END_USER}
                checked={selectedUserType === TypeOfUser.END_USER}
                className="sr-only"
                disabled={!!id}
                readOnly
                data-testid="user-type-option-end-user-input"
              />
              <span className="flex flex-col text-md">
                <span className="font-bold">{t('ec_flow_texts.end_user')}</span>
                <span className="font-medium text-gray-3">
                  {t('ec_flow_texts.original_receiver')}
                </span>
              </span>
            </label>
            <label
              onClick={() => {
                navigate(
                  `/campaigns/ec-flow/${id}/${TypeOfUser.REFERRAL_USER}/home`,
                )
                setSelectedUserType(TypeOfUser.REFERRAL_USER)
              }}
              className={cx(
                'flex flex-1 cursor-pointer rounded-lg border-2 border-border bg-white p-2 shadow-sm focus:outline-none hover:border-secondary',
                {
                  'border-black-100':
                    selectedUserType === TypeOfUser.REFERRAL_USER,
                },
              )}
              data-testid="user-type-option-referral"
            >
              <input
                name={TypeOfUser.REFERRAL_USER}
                type="radio"
                value={TypeOfUser.REFERRAL_USER}
                checked={selectedUserType === TypeOfUser.REFERRAL_USER}
                className="sr-only"
                disabled={!!id}
                readOnly
                data-testid="user-type-option-referral-input"
              />
              <span className="flex flex-col text-md">
                <span className="font-bold">{t('ec_flow_texts.referral')}</span>
                <span className="font-medium text-gray-3">
                  {t('ec_flow_texts.invited')}
                </span>
              </span>
            </label>
          </div>
        )}

        <FormInput
          name="header"
          control={control}
          label={t('ec_flow_texts.header')}
          labelClassName="text-sm ext-slate-50"
          type="text"
          inputClassName="w-full rounded border border-border px-3 py-2"
          testId="header"
        />

        {shouldShowTextField && (
          <div className="flex flex-col gap-1 my-4">
            <label className="text-sm text-slate-50" htmlFor="text">
              {t('ec_flow_texts.text')}
            </label>
            <div data-testid="text">
              <WYSIWYG
                name="text"
                control={control}
                className="w-full h-64"
                maxChars={150}
                customerId={campaign.customerId ?? undefined}
                endUserTheme={campaign.customer?.endUserTheme}
              />
            </div>
          </div>
        )}

        {isQuestionnaireScreen && (
          <>
            <FormInput
              name="termsAndConditions"
              control={control}
              label={t('ec_flow_texts.termsAndConditions')}
              labelClassName="text-md text-grey-100"
              type="text"
              inputClassName="w-full rounded border border-border px-3 py-2"
              testId="termsAndConditions"
            />

            <FormInput
              name="termsAndConditionsEndUser"
              control={control}
              label={
                selectedUserType === TypeOfUser.END_USER
                  ? t('ec_flow_texts.termsAndConditionsEndUser')
                  : t('ec_flow_texts.termsAndConditionsReferral')
              }
              labelClassName="text-md text-grey-100"
              type="text"
              inputClassName="w-full rounded border border-border px-3 py-2"
              testId="termsAndConditionsEndUser"
            />

            <FormInput
              name="termsAndConditionsReferral"
              control={control}
              label={t('ec_flow_texts.termsAndConditionsMarketing')}
              labelClassName="text-md text-grey-100"
              type="text"
              inputClassName="w-full rounded border border-border px-3 py-2"
              testId="termsAndConditionsReferral"
            />

            <FormInput
              name="termsAndConditionsSummary"
              control={control}
              label={t('ec_flow_texts.termsAndConditionsSummary')}
              labelClassName="text-md text-grey-100"
              type="text"
              inputClassName="w-full rounded border border-border px-3 py-2"
              testId="termsAndConditionsSummary"
            />
          </>
        )}

        {(visibleButtonKeys.length > 0 || showErrorLanguage) && (
          <div className="mt-6">
            <h3 className="text-md font-bold">
              {t('ec_flow_texts.buttons_title')}
            </h3>
            <p className="text-sm text-zinc-500 mb-2">
              {t('ec_flow_texts.buttons_help')}
            </p>
            {visibleButtonKeys.map((key) => (
              <FormInput
                key={key}
                name={`buttons.${key}`}
                control={control}
                label={t(`ec_flow_texts.button_${key}`)}
                labelClassName="text-sm text-slate-50"
                type="text"
                placeholder={standardButtonText(key)}
                inputClassName="w-full rounded border border-border px-3 py-2"
                testId={`button-${key}`}
              />
            ))}
            {showErrorLanguage && (
              <FormSelect
                name="errorLanguage"
                control={control}
                label={t('ec_flow_texts.error_language')}
                labelClassName="text-sm text-slate-50"
                options={ecFlowErrorLanguageSchema.options.map((lang) => ({
                  value: lang,
                  label: t(`ec_flow_texts.error_language_${lang}`),
                }))}
                testId="error-language"
              />
            )}
          </div>
        )}

        <div
          className={cx(
            previousScreenIndex < 0
              ? 'flex justify-end w-full'
              : 'flex justify-between w-full',
          )}
        >
          {previousScreenIndex >= 0 && (
            <div className="py-8 my-10">
              <Button
                onClick={() =>
                  navigate(
                    `/campaigns/ec-flow/${id}/${selectedUserType}/${previousScreenName}`,
                  )
                }
                type="button"
                label={t('previous_screen')}
                data-testid="back"
                IconLeft={CaretLeft}
              />
            </div>
          )}
          <CampaignButtons
            submitLabel={t('next')}
            saveDraftLabel={t('save')}
            disableSubmit={
              pathname.split('/').pop() === ECFlowScreensType.EXPIRED
            }
            disableSaveDraft={false}
            saveDraftTestId="preview-save-draft"
            submitTestId="preview-submit"
          />
        </div>
      </form>
    </div>
  )
}
